import get from 'lodash/get';
import flatten from 'lodash/flatten';

import { activityTypes, stepTypes, contentTypes, inputTypes } from '../step/step-constants';
import { stepModel } from '../step/step-model';

/* Determining functions ------------------------- */

function isConversation(activityType) {
  return activityType.includes(activityTypes.CONVERSATION);
}

function isMultipleChoice(step) {
  return step.type === stepTypes.MULTIPLE_CHOICE;
}

function isMultipleChoiceSpeech(step) {
  return step.type === stepTypes.MULTIPLE_CHOICE && get(step, 'behavior.inputType') === inputTypes.SPEAKING;
}

function hasMultipleAnswers(behavior) {
  return get(behavior, 'evaluationType', 'any') === 'all';
}

/* Helper functions ------------------------------ */

function isAudioImageOnly(prompts, choices) {
  var promptTypes = stepModel.getContentTypes(prompts);
  var audioOnlyPrompt = promptTypes.length === 1 && promptTypes[0] === contentTypes.AUDIOS;
  // all choices have same data, so we can determine type from first one
  var choicesTypes = stepModel.getContentTypes(choices[0]);
  var imageOnlyChoice = choicesTypes.length && choicesTypes[0] === contentTypes.IMAGES;
  return audioOnlyPrompt && imageOnlyChoice;
}

function shouldHideText(behavior) {
  if (behavior && behavior.presentation) {
    return behavior.presentation.includes('hiddenResponseText');
  }
  return false;
}

// Helper for speech type.
function hasContentChunkData(content) {
  return content.every(function (obj) {
    return obj.contentChunks;
  });
}

/* Speech type helper.
 * We need to check for and normalize content containing 'chunked' data.
 * Chunked data can include *multiple* texts and audios for one answer choice
 * This can be seen in the activity 'DialogueExpressionWithReco'.
 */
function normalizeChunkedData(content) {
  if (!Array.isArray(content) || !content.length) {
    return content;
  }
  return content.filter(function (_ref) {
    var contentChunks = _ref.contentChunks;
    return !!contentChunks;
  }).map(function (_ref2) {
    var id = _ref2.id,
        contentChunks = _ref2.contentChunks;
    return {
      id: id,
      text: contentChunks.map(function (c) {
        return c.text;
      }).join(' '),
      audios: contentChunks.map(function (c) {
        return c.audios;
      }),
      chunkedIds: contentChunks.map(function (c) {
        return c.id;
      }),
      type: 'normalized'
    };
  });
}

/* Parser functions -------------------------------- */

/* Parse and store content data by type. Used in withStepData. */
function parseMultipleChoiceData(step, forceShuffle) {
  var content = step.content;
  var shuffle = stepModel.isRandomOrdering(step);
  var prompts = flatten(content[0]);
  var choices = hasContentChunkData(content[1]) ? normalizeChunkedData(content[1]) : content[1];
  return {
    choices: shuffle ? stepModel.shuffleChoices(choices, forceShuffle) : choices,
    prompts: {
      audios: stepModel.parseContentByType(contentTypes.AUDIOS, prompts),
      images: stepModel.parseContentByType(contentTypes.IMAGES, prompts),
      localizations: stepModel.parseContentByType(contentTypes.LOCALIZATIONS, prompts),
      text: stepModel.parseContentByType(contentTypes.TEXT, prompts),
      videos: stepModel.parseContentByType(contentTypes.VIDEOS, prompts)
    }
  };
}

var isCorrect = function isCorrect(step, answer) {
  var correctAnswers = step.correct;

  return correctAnswers.includes(answer);
};

var areAnswersCorrect = function areAnswersCorrect() {
  var answers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var step = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!answers.length) {
    return false;
  }
  if (hasMultipleAnswers(step.behavior)) {
    return step.correct.length === answers.length && answers.every(function (answer) {
      return !!answer.correct;
    });
  } else {
    return answers.every(function (answer) {
      return !!answer.correct;
    });
  }
};

var getAnswers = function getAnswers(step, values) {
  return values.map(function (answer) {
    var correct = isCorrect(step, answer);
    return { answer: answer, correct: correct };
  });
};

export var stepMultipleChoiceModel = {
  isConversation: isConversation,
  isMultipleChoice: isMultipleChoice,
  isMultipleChoiceSpeech: isMultipleChoiceSpeech,
  hasMultipleAnswers: hasMultipleAnswers,
  isAudioImageOnly: isAudioImageOnly,
  shouldHideText: shouldHideText,
  parseMultipleChoiceData: parseMultipleChoiceData,
  isCorrect: isCorrect,
  areAnswersCorrect: areAnswersCorrect,
  getAnswers: getAnswers
};